import { Quill } from "react-quill-new";

const PATCHED_KEY = "__table_better_column_resize_patched__";
/** Grab radius around a vertical cell border. Library default is 5px. */
const COLUMN_EDGE_PX = 12;
const MIN_COLUMN_PX = 28;

type ColBlot = {
    domNode: HTMLElement;
    next: ColBlot | null;
};

type ColgroupLike = {
    children: { head: ColBlot | null };
};

type TableBlotLike = {
    domNode: HTMLTableElement;
    colgroup?: () => ColgroupLike | null;
};

type CellBlotLike = {
    table?: () => TableBlotLike;
};

type MousePosition = {
    clientX: number;
    clientY: number;
};

type OperateOptions = {
    tableNode: HTMLElement;
    cellNode: Element;
    mousePosition: MousePosition;
};

type OperatePropertyResult = {
    dragBlockProps?: Record<string, string>;
    containerProps?: Record<string, string>;
    lineProps?: Record<string, string>;
};

type OperateLineProto = {
    quill: { container: HTMLElement };
    direction: string | null;
    drag: boolean;
    options: OperateOptions | null;
    line: HTMLElement | null;
    getLevelColSum: (cell: Element) => number;
    setColWidth: (domNode: HTMLElement, width: string, isPercent: boolean) => void;
    getProperty: (options: OperateOptions) => OperatePropertyResult;
    updateProperty: (options: OperateOptions) => void;
    handleMouseMove: (event: MouseEvent) => void;
    setCellLevelRect: (cell: Element, clientX: number) => void;
};

const columnPercent = (px: number, tableWidth: number) =>
    `${((px / Math.max(1, tableWidth)) * 100).toFixed(2)}%`;

const measureRowColumns = (row: Element): number[] => {
    const widths: number[] = [];
    for (const cell of Array.from(row.children)) {
        if (!(cell instanceof HTMLElement)) continue;
        if (cell.tagName !== "TD" && cell.tagName !== "TH") continue;
        const span = Math.max(1, Number(cell.getAttribute("colspan")) || 1);
        const each = cell.getBoundingClientRect().width / span;
        for (let i = 0; i < span; i += 1) widths.push(each);
    }
    return widths;
};

/** Approach from either side of a vertical border maps to the cell on its left. */
const resolveColumnEdgeOptions = (options: OperateOptions): OperateOptions => {
    const cell = options.cellNode;
    if (!(cell instanceof HTMLElement)) return options;
    const rect = cell.getBoundingClientRect();
    const distRight = Math.abs(rect.right - options.mousePosition.clientX);
    const distLeft = Math.abs(rect.left - options.mousePosition.clientX);
    const previous = cell.previousElementSibling;
    if (
        distLeft <= COLUMN_EDGE_PX &&
        distLeft <= distRight &&
        previous instanceof HTMLElement &&
        (previous.tagName === "TD" || previous.tagName === "TH")
    ) {
        return {
            ...options,
            cellNode: previous,
            mousePosition: {
                ...options.mousePosition,
                clientX: previous.getBoundingClientRect().right,
            },
        };
    }
    if (distRight <= COLUMN_EDGE_PX) {
        return {
            ...options,
            mousePosition: {
                ...options.mousePosition,
                clientX: rect.right,
            },
        };
    }
    return options;
};

const lockTableFullWidth = (table: HTMLTableElement) => {
    table.style.setProperty("width", "100%");
    const temporary = table.querySelector("temporary");
    if (temporary instanceof HTMLElement) {
        temporary.style.setProperty("width", "100%");
    }
};

const applyColumnWidths = (
    line: OperateLineProto,
    table: HTMLTableElement,
    colgroup: ColgroupLike | null,
    widths: number[],
) => {
    const tableWidth = Math.max(1, table.getBoundingClientRect().width);
    if (colgroup) {
        let col = colgroup.children.head;
        for (const width of widths) {
            if (!col) break;
            line.setColWidth(col.domNode, String(Math.round(width)), true);
            col = col.next;
        }
        return;
    }
    for (const row of Array.from(table.rows)) {
        let cursor = 0;
        for (const cell of Array.from(row.cells)) {
            const span = Math.max(1, Number(cell.getAttribute("colspan")) || 1);
            if (span === 1 && cursor < widths.length) {
                cell.style.setProperty("width", columnPercent(widths[cursor], tableWidth));
            }
            cursor += span;
        }
    }
};

/**
 * quill-table-better resizes columns from a 5px hit zone and reads `<col>` box size,
 * which is often 0. Dragging the last border also grows the whole table.
 * Keep the table at 100% and move width between neighboring columns.
 */
export const patchTableColumnResize = (operateLine: object | null | undefined) => {
    if (!operateLine) return;
    const proto = Object.getPrototypeOf(operateLine) as OperateLineProto & Record<string, unknown>;
    if (!proto || proto[PATCHED_KEY]) return;

    const originalGetProperty = proto.getProperty;
    const originalSetColWidth = proto.setColWidth;
    const originalHandleMouseMove = proto.handleMouseMove;
    const originalUpdateProperty = proto.updateProperty;

    proto.getProperty = function patchedGetProperty(this: OperateLineProto, options: OperateOptions) {
        const resolved = resolveColumnEdgeOptions(options);
        const result = originalGetProperty.call(this, resolved);
        if (result?.containerProps && this.direction === "level") {
            const edgeCell = resolved.cellNode;
            const edgeX = edgeCell.getBoundingClientRect().right;
            const containerLeft = this.quill.container.getBoundingClientRect().left;
            result.containerProps.width = `${COLUMN_EDGE_PX}px`;
            result.containerProps.left = `${edgeX - containerLeft - COLUMN_EDGE_PX / 2}px`;
        }
        return result;
    };

    proto.updateProperty = function patchedUpdateProperty(this: OperateLineProto, options: OperateOptions) {
        originalUpdateProperty.call(this, resolveColumnEdgeOptions(options));
    };

    proto.handleMouseMove = function patchedHandleMouseMove(this: OperateLineProto, event: MouseEvent) {
        originalHandleMouseMove.call(this, event);
        if (!this.options?.cellNode || this.drag) return;
        const resolved = resolveColumnEdgeOptions(this.options);
        if (resolved.cellNode !== this.options.cellNode) this.options = resolved;
    };

    proto.setColWidth = function patchedSetColWidth(
        this: OperateLineProto,
        domNode: HTMLElement,
        width: string,
        isPercent: boolean,
    ) {
        if (!isPercent) {
            originalSetColWidth.call(this, domNode, width, isPercent);
            return;
        }
        const px = parseFloat(width);
        if (!Number.isFinite(px) || px <= 0) return;
        const table = domNode.closest("table");
        const basis = Math.max(1, table?.getBoundingClientRect().width || px);
        domNode.style.setProperty("width", columnPercent(px, basis));
    };

    proto.setCellLevelRect = function patchedSetCellLevelRect(
        this: OperateLineProto,
        cell: Element,
        clientX: number,
    ) {
        if (!(cell instanceof HTMLElement) || !cell.parentElement) return;
        const table = cell.closest("table");
        if (!table) return;
        const widths = measureRowColumns(cell.parentElement);
        const index = this.getLevelColSum(cell) - 1;
        if (index < 0 || index >= widths.length) return;
        const partner = index < widths.length - 1 ? index + 1 : index - 1;
        if (partner < 0) return;

        const change = Math.trunc(clientX - cell.getBoundingClientRect().right);
        if (!change) return;

        let nextIndex = widths[index] + change;
        let nextPartner = widths[partner] - change;
        if (nextIndex < MIN_COLUMN_PX) {
            nextPartner -= MIN_COLUMN_PX - nextIndex;
            nextIndex = MIN_COLUMN_PX;
        }
        if (nextPartner < MIN_COLUMN_PX) {
            nextIndex -= MIN_COLUMN_PX - nextPartner;
            nextPartner = MIN_COLUMN_PX;
        }
        if (nextIndex < MIN_COLUMN_PX || nextPartner < MIN_COLUMN_PX) return;

        widths[index] = nextIndex;
        widths[partner] = nextPartner;

        const cellBlot = Quill.find(cell) as CellBlotLike | null;
        const colgroup = cellBlot?.table?.().colgroup?.() ?? null;
        applyColumnWidths(this, table, colgroup, widths);
        lockTableFullWidth(table);
    };

    proto[PATCHED_KEY] = true;
};
